import {
  ElementRef,
  Component,
  ChangeDetectorRef,
  ViewChild,
  Renderer2,
  HostListener,
  OnDestroy,
} from '@angular/core';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { TemplateEditorUtilsService } from '../../services/template-editor-utils.service';
import { AnalyticsFactory } from 'src/app/ajs-upgraded-providers';
import * as _ from 'lodash';
import { PresentationUtilsService } from 'src/app/presentations/services/presentation-utils.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { CustomFontListManagementService } from '../services/custom-font-list-management.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { getImproveWithAiLabel } from '../text-ai/text-ai.component';

export interface RiseFont {
  name: string;
  font: string;
  url?: string;
  failedToLoad?: boolean;
}

export enum TextView {
  MAIN = 'main',
  IMPROVE_WITH_AI = 'improve_with_ai',
}

@Component({
  selector: 'template-component-text',
  templateUrl: './text.component.html',
  standalone: false,
})
export class TextComponent implements OnDestroy {
  public readonly TextView = TextView;

  public readonly positionOptions = [
    {
      name: 'Top',
      vertical: 'top',
    },
    {
      name: 'Middle',
      vertical: 'middle',
    },
    {
      name: 'Bottom',
      vertical: 'bottom',
    },
  ];

  public readonly formattingOptions = ['bold', 'italic', 'underline'];

  public readonly alignmentOptions = ['left', 'center', 'right'];

  public readonly defaultFont: RiseFont = {
    name: 'Default',
    font: '',
    url: '',
  };

  public readonly googleFontsBaseUrl =
    'https://fonts.googleapis.com/css2?display=swap&family=';

  public readonly googleFonts: RiseFont[] = [
    { name: 'Bebas Neue', font: 'Bebas Neue, sans-serif' },
    { name: 'Caveat', font: 'Caveat, cursive' },
    { name: 'Fira Code', font: 'Fira Code, monospace' },
    { name: 'Inter', font: 'Inter, sans-serif' },
    { name: 'JetBrains Mono', font: 'JetBrains Mono, monospace' },
    { name: 'Lato', font: 'Lato, sans-serif' },
    { name: 'Lora', font: 'Lora, serif' },
    { name: 'Merriweather', font: 'Merriweather, serif' },
    { name: 'Montserrat', font: 'Montserrat, sans-serif' },
    { name: 'Open Sans', font: 'Open Sans, sans-serif' },
    { name: 'Oswald', font: 'Oswald, sans-serif' },
    { name: 'Pacifico', font: 'Pacifico, cursive' },
    { name: 'Playfair Display', font: 'Playfair Display, serif' },
    { name: 'Poppins', font: 'Poppins, sans-serif' },
    { name: 'Raleway', font: 'Raleway, sans-serif' },
    { name: 'Roboto', font: 'Roboto, sans-serif' },
    { name: 'Source Code Pro', font: 'Source Code Pro, monospace' },
  ];

  public readonly systemFonts: RiseFont[] = [
    { name: 'Andale Mono', font: 'Andale Mono, monospace' },
    { name: 'Arial', font: 'Arial, sans-serif' },
    { name: 'Arial Black', font: 'Arial Black, sans-serif' },
    { name: 'Book Antiqua', font: 'Book Antiqua, serif' },
    { name: 'Comic Sans MS', font: 'Comic Sans MS, sans-serif' },
    { name: 'Courier New', font: 'Courier New, monospace' },
    { name: 'Georgia', font: 'Georgia, serif' },
    { name: 'Helvetica', font: 'Helvetica, sans-serif' },
    { name: 'Impact', font: 'Impact, sans-serif' },
    { name: 'Lucida Console', font: 'Lucida Console, monospace' },
    { name: 'Symbol', font: 'symbol' },
    { name: 'Tahoma', font: 'Tahoma, sans-serif' },
    { name: 'Terminal', font: 'Terminal, monospace' },
    { name: 'Times New Roman', font: 'Times New Roman, serif' },
    { name: 'Trebuchet MS', font: 'Trebuchet MS, sans-serif' },
    { name: 'Verdana', font: 'Verdana, sans-serif' },
  ];

  public readonly fontPickerMaxHeight: number = 250;

  componentId;
  value;
  showPosition;
  position;
  fontsize;
  fontsizePlaceholder;
  minfontsize;
  maxfontsize;
  isMultiline;
  isFontPickerOpen: boolean = false;
  activeOptionId: string | null = null;
  font;
  fontUrl;
  fontName;
  formatting;
  color;
  lineheight;
  letterspacing;
  alignment;
  isTextVisible;
  isPlaylistItem;
  handleMessageBind;
  lastTrackedPropertyName;
  customFonts: RiseFont[] = [];
  fontFailedToLoad: boolean = false;
  fontRemovedFromList: boolean = false;
  currentView: TextView = TextView.MAIN;
  @ViewChild('fontPicker') fontPicker: ElementRef;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private componentsFactory: ComponentsService,
    private attributeDataFactory: AttributeDataService,
    private templateEditorService: TemplateEditorService,
    private templateEditorUtils: TemplateEditorUtilsService,
    private changeDetectorRef: ChangeDetectorRef,
    private presentationUtils: PresentationUtilsService,
    private analyticsFactory: AnalyticsFactory,
    private companyStateService: CompanyStateService,
    private customFontListManagementService: CustomFontListManagementService,
    private aiCreditsService: AiCreditsService,
  ) {
    componentsFactory.registerDirective({
      type: 'rise-text',
      element: this.elementRef.nativeElement,
      show: (options) => {
        this.componentId = this.componentsFactory.selected.id;
        this.currentView = TextView.MAIN;

        if (this.fontPicker) {
          this.renderer.setStyle(
            this.fontPicker.nativeElement,
            'maxHeight',
            this.fontPickerMaxHeight + 'px',
          );
        }

        this.isPlaylistItem = options?.playlist;

        // make the text input visible all the time until layering issue is fixed in all templates
        // see https://github.com/Rise-Vision/html-template-library/issues/3143
        const layeringIssue = true;
        this.isTextVisible =
          layeringIssue ||
          this.isPlaylistItem ||
          this.presentationUtils.isMobileBrowser();

        this._load();
      },
      getName: (componentId) => {
        return this.attributeDataFactory.getAvailableAttributeData(
          componentId,
          'value',
        );
      },
      onBackHandler: () => {
        if (this.currentView === TextView.IMPROVE_WITH_AI) {
          this.closeImproveWithAI();
          return true;
        }

        return false;
      },
      getLabel: () => {
        if (this.currentView === TextView.IMPROVE_WITH_AI) {
          return this.textAiButtonLabel;
        }
      },
      usesBackTitleHeader: () => this.currentView === TextView.IMPROVE_WITH_AI,
      backToMainView: () => {
        if (this.currentView === TextView.IMPROVE_WITH_AI) {
          this.closeImproveWithAI();
        }
      },
      onAiHandler: () => {
        if (!this.canShowImproveWithAI()) {
          return false;
        }

        this.openImproveWithAI();
        return true;
      },
    });

    this.handleMessageBind = this.handleMessageFromRiseText.bind(this);
    window.addEventListener('message', this.handleMessageBind);

    this.customFontListManagementService.onFontAddedHandler =
      this._loadCustomFonts.bind(this);
    this.customFontListManagementService.onFontSelectedHandler =
      this.onFontPickerOptionClick.bind(this);
  }

  ngOnDestroy() {
    this.customFontListManagementService.onFontAddedHandler = null;
    this.customFontListManagementService.onFontSelectedHandler = null;
  }

  _loadAlignment() {
    const textalign = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'textalign',
    );
    const vertical = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'verticalalign',
    );
    const horizontal = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'horizontalalign',
    );

    this.position = _.find(this.positionOptions, (o) => {
      return o.vertical === vertical;
    });
    this.showPosition = !!this.position;

    // ensure backwards compatibility by falling back to horizontalalign
    this.alignment = textalign
      ? textalign
      : this.showPosition
        ? horizontal
        : '';
  }

  _load() {
    this._loadGoogleFonts();
    this._loadCustomFonts();

    this.isMultiline = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'multiline',
    );
    const value = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'value',
    );
    const fontsize = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'fontsize',
    );
    const minfontsize = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'minfontsize',
    );
    const maxfontsize = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'maxfontsize',
    );
    const font = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'font',
    );
    const fontUrl = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'fontUrl',
    );
    const fontName = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'fontName',
    );
    const formatting = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'formatting',
    );
    const color = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'color',
    );
    const lineheight = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'lineheight',
    );
    const letterspacing = this.attributeDataFactory.getAvailableAttributeData(
      this.componentId,
      'letterspacing',
    );
    const fontSelected = !font
      ? this.defaultFont
      : this.customFonts
          .concat(this.googleFonts)
          .concat(this.systemFonts)
          .find((fontFamily) => fontFamily.font === font);

    const fontsizeInt = this.templateEditorUtils.intValueFor(fontsize, null);
    const letterspacingInt = this.templateEditorUtils.floatValueFor(
      letterspacing,
      null,
    );
    this.minfontsize = this.templateEditorUtils.intValueFor(minfontsize, 1);
    this.maxfontsize = this.templateEditorUtils.intValueFor(maxfontsize, 200);
    this.fontsizePlaceholder =
      this.attributeDataFactory.getBlueprintData(
        this.componentId,
        'fontsize',
      ) || 'Default';

    this.value = value;
    this.fontsize = fontsizeInt;
    this.font = fontSelected ? fontSelected.font : font;
    this.fontName = fontSelected ? fontSelected.name : fontName;
    this.fontUrl = fontSelected ? fontSelected.url : fontUrl;
    this.fontFailedToLoad = fontSelected ? fontSelected.failedToLoad : false;
    this.fontRemovedFromList = !fontSelected;
    this.formatting = formatting ? formatting : '';
    this.color = color ? color : '';
    this.lineheight = lineheight ? lineheight.toString() : '';
    this.letterspacing = letterspacingInt;

    this._loadAlignment();
  }

  private _loadGoogleFonts() {
    const url =
      this.googleFontsBaseUrl +
      this.googleFonts
        .map((googleFont) => googleFont.name.replaceAll(' ', '+'))
        .join('&family=');

    const fontAlreadyLoaded = Array.from(
      document.head.querySelectorAll('link'),
    ).some((link) => link.href === url);

    if (fontAlreadyLoaded) {
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    link.onload = () => {
      // Google Fonts loaded successfully
    };

    link.onerror = (error) => {
      console.error('Google Fonts loading failed:', { url, error });
    };

    document.head.appendChild(link);
  }

  private _isRiseMediaLibraryPath(
    storagePath: string | null | undefined,
  ): boolean {
    if (!storagePath) {
      return false;
    }
    return (
      storagePath.startsWith('risemedialibrary-') &&
      storagePath.indexOf('/') > 0
    );
  }

  private _encodePath(filePath) {
    // encode each element of the path separately
    const encodedPath = filePath
      .split('/')
      .map((pathElement) => encodeURIComponent(pathElement))
      .join('/');

    return encodedPath;
  }

  private _constructCustomFontUrl(url: string) {
    if (url) {
      if (this._isRiseMediaLibraryPath(url)) {
        const encodedPath = this._encodePath(url);

        return `https://storage.googleapis.com/${encodedPath}`;
      }
      return url;
    }
    return '';
  }

  private async _loadCustomFonts() {
    const customFonts = this.customFontListManagementService.getFonts();
    this.customFonts = customFonts.map((font) => ({
      name: font.name,
      font: font.font,
      url: font.url,
    }));

    await Promise.all(
      this.customFonts.map((font) => this._loadCustomFontByUrl(font)),
    );
  }

  private async _loadCustomFontByUrl(customFont: RiseFont) {
    const fontAlreadyLoaded = Array.from(document.fonts).some(
      (font) => font.family === customFont.font,
    );

    if (fontAlreadyLoaded) {
      return;
    }

    const constructedUrl = this._constructCustomFontUrl(customFont.url);
    // Escape parentheses in the URL for CSS url() function
    const escapedUrl = constructedUrl.replace(/[()]/g, '\\$&');
    const font = new FontFace(customFont.font, `url(${escapedUrl})`);
    try {
      const loadedFont = await font.load();
      document.fonts.add(loadedFont);
      // Custom Font loaded successfully
    } catch (error) {
      console.error('Custom Font loading failed:', {
        url: customFont.url,
        error,
      });
      customFont.failedToLoad = true;
      if (this.font === customFont.font) {
        this.fontFailedToLoad = true;
        this.refreshUI();
      }
    }
  }

  handleMessageFromRiseText(event) {
    let data = event.data;

    if ('string' === typeof event.data) {
      try {
        data = JSON.parse(event.data);
      } catch (e) {}
    }

    if (
      data.type === 'rise-text-change' &&
      (this.componentId === data.id || this.isPlaylistItem)
    ) {
      this.value = data.value;
      this.save('value');
    }
  }

  refreshUI() {
    this.changeDetectorRef.detectChanges();
  }

  canShowImproveWithAI(): boolean {
    return (
      this.aiCreditsService.canUserUseAI() &&
      this.companyStateService.isAiEnabled()
    );
  }

  get textAiButtonLabel(): string {
    return getImproveWithAiLabel(this.value);
  }

  openImproveWithAI() {
    this.currentView = TextView.IMPROVE_WITH_AI;
  }

  closeImproveWithAI() {
    this.currentView = TextView.MAIN;
  }

  onAiTextGenerated(newText: string) {
    this.value = newText;
    this.save('value');
  }

  save(propertyName) {
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'value',
      this.value,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'font',
      this.font,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'fontUrl',
      this.fontUrl,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'fontName',
      this.fontName,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'formatting',
      this.formatting,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'color',
      this.color,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'fontsize',
      this.fontsize,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'lineheight',
      this.lineheight ? parseFloat(this.lineheight) : '',
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'letterspacing',
      this.letterspacing,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'textalign',
      this.alignment,
    );
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'horizontalalign',
      this.alignment,
    );

    if (this.showPosition) {
      this.attributeDataFactory.setAttributeData(
        this.componentId,
        'verticalalign',
        this.position.vertical,
      );
    }
    this.refreshUI();

    if (propertyName && propertyName !== this.lastTrackedPropertyName) {
      this.lastTrackedPropertyName = propertyName;
      this.analyticsFactory.track('Rise Text Property Updated', {
        companyId: this.companyStateService.getSelectedCompanyId(),
        propertyName,
      });
    }
  }

  closeFontPicker() {
    this.isFontPickerOpen = false;
    this.activeOptionId = null;
  }

  onFontPickerOptionClick(fontFamily: any) {
    this.fontRemovedFromList = false;
    this.font = fontFamily.font;
    this.fontName = fontFamily.name;
    this.fontUrl = fontFamily.url;
    this.fontFailedToLoad = fontFamily.failedToLoad;

    this.save('font');
  }

  onFontPickerKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.closeFontPicker();
        event.preventDefault();
        break;
      case 'Enter':
      case ' ':
        if (!this.isFontPickerOpen) {
          this.onFontPickerClick();
        } else if (this.activeOptionId) {
          this.selectActiveOption();
        }
        event.preventDefault();
        break;
      case 'ArrowDown':
        if (!this.isFontPickerOpen) {
          this.onFontPickerClick();
        } else {
          this.navigateOptions('down');
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (this.isFontPickerOpen) {
          this.navigateOptions('up');
          event.preventDefault();
        }
        break;
      case 'Home':
        if (this.isFontPickerOpen) {
          this.navigateToFirstOption();
          event.preventDefault();
        }
        break;
      case 'End':
        if (this.isFontPickerOpen) {
          this.navigateToLastOption();
          event.preventDefault();
        }
        break;
      case 'Tab':
        if (this.isFontPickerOpen) {
          this.closeFontPicker();
        }
        break;
      default:
        return;
    }
  }

  onFontPickerAddFontClick() {
    this.componentsFactory.editComponent({
      type: 'rise-custom-font-selector',
    });
  }

  onFormattingClick(option: string): void {
    if (this.formatting) {
      const formats = this.formatting.split(',');
      const index = formats.indexOf(option);

      if (index !== -1) {
        formats.splice(index, 1);
      } else {
        formats.push(option);
      }

      this.formatting = formats.toString();
    } else {
      this.formatting = option;
    }

    this.save('formatting');
  }

  isFormatSelected(option: string): boolean {
    if (!this.formatting) {
      return false;
    }

    const formats = this.formatting.split(',');

    return formats.includes(option);
  }

  isValidFontSize() {
    if (!this.fontsize) {
      return true;
    }

    return (
      this.fontsize >= this.minfontsize && this.fontsize <= this.maxfontsize
    );
  }

  onColorChange() {
    this.attributeDataFactory.setAttributeData(
      this.componentId,
      'color',
      this.color,
    );
  }

  onAlignmentClick(option: string): void {
    this.alignment = option;

    this.save('alignment');
  }

  isAlignmentSelected(option: string): boolean {
    return this.alignment && this.alignment === option;
  }

  onFontPickerClick() {
    this.isFontPickerOpen = !this.isFontPickerOpen;
    if (this.isFontPickerOpen) {
      this.setActiveOptionToCurrentFont();
    }
  }

  onFontPickerBlur(event: FocusEvent) {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const fontPickerContainer = event.target as HTMLElement;

      const isChildOfFontPicker =
        activeElement &&
        (fontPickerContainer.contains(activeElement) ||
          activeElement.closest('.font-picker-dropdown'));

      if (!isChildOfFontPicker) {
        this.closeFontPicker();
        this.refreshUI();
      }
    }, 0);
  }

  private getAllFontOptions(): RiseFont[] {
    return [this.defaultFont]
      .concat(this.customFonts)
      .concat(this.googleFonts)
      .concat(this.systemFonts);
  }

  private getAllNavigableOptions(): string[] {
    // Return array of option IDs in navigation order
    const options = ['font-option-default', 'font-option-add-font'];

    // Add custom fonts
    for (let i = 0; i < this.customFonts.length; i++) {
      options.push(`font-option-custom-${i}`);
    }

    // Add Google fonts
    for (let i = 0; i < this.googleFonts.length; i++) {
      options.push(`font-option-google-${i}`);
    }

    // Add system fonts
    for (let i = 0; i < this.systemFonts.length; i++) {
      options.push(`font-option-system-${i}`);
    }

    return options;
  }

  private getOptionIdForFont(fontFamily: RiseFont): string {
    if (fontFamily === this.defaultFont) {
      return 'font-option-default';
    }

    const customIndex = this.customFonts.indexOf(fontFamily);
    if (customIndex !== -1) {
      return `font-option-custom-${customIndex}`;
    }

    const googleIndex = this.googleFonts.indexOf(fontFamily);
    if (googleIndex !== -1) {
      return `font-option-google-${googleIndex}`;
    }

    const systemIndex = this.systemFonts.indexOf(fontFamily);
    if (systemIndex !== -1) {
      return `font-option-system-${systemIndex}`;
    }

    return 'font-option-default';
  }

  private findOptionIndex(optionId: string): number {
    const allOptions = this.getAllNavigableOptions();
    return allOptions.indexOf(optionId);
  }

  private navigateOptions(direction: 'up' | 'down'): void {
    const allOptions = this.getAllNavigableOptions();
    const currentIndex = this.activeOptionId
      ? this.findOptionIndex(this.activeOptionId)
      : -1;

    let newIndex: number;
    if (direction === 'down') {
      newIndex = currentIndex < allOptions.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allOptions.length - 1;
    }

    this.setActiveOptionByIndex(newIndex);
  }

  private navigateToFirstOption(): void {
    this.setActiveOptionByIndex(0);
  }

  private navigateToLastOption(): void {
    const allOptions = this.getAllNavigableOptions();
    this.setActiveOptionByIndex(allOptions.length - 1);
  }

  private setActiveOptionByIndex(index: number): void {
    const allOptions = this.getAllNavigableOptions();
    if (index >= 0 && index < allOptions.length) {
      this.activeOptionId = allOptions[index];
      setTimeout(() => this.scrollActiveOptionIntoView(), 0);
    }
  }

  private selectActiveOption(): void {
    if (!this.activeOptionId) return;

    if (this.activeOptionId === 'font-option-add-font') {
      this.onFontPickerAddFontClick();
      this.closeFontPicker();
    } else {
      const allFonts = this.getAllFontOptions();
      const selectedFont = allFonts.find(
        (font) => this.getOptionIdForFont(font) === this.activeOptionId,
      );

      if (selectedFont) {
        this.onFontPickerOptionClick(selectedFont);
        this.closeFontPicker();
      }
    }
  }

  private setActiveOptionToCurrentFont(): void {
    const allFonts = this.getAllFontOptions();
    const currentFont = allFonts.find((font) => font.font === this.font);

    if (currentFont) {
      this.activeOptionId = this.getOptionIdForFont(currentFont);
    } else {
      this.activeOptionId = 'font-option-default';
    }

    setTimeout(() => this.scrollActiveOptionIntoView(), 0);
  }

  private scrollActiveOptionIntoView(): void {
    if (!this.activeOptionId) return;

    const activeElement = document.getElementById(this.activeOptionId);
    const dropdownContainer = document.getElementById('font-picker-listbox');

    if (activeElement && dropdownContainer) {
      const containerRect = dropdownContainer.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();

      const isAbove = elementRect.top < containerRect.top;
      const isBelow = elementRect.bottom > containerRect.bottom;

      if (isAbove || isBelow) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }

  @HostListener('document:click', ['$event.target'])
  onClick(targetElement: HTMLElement) {
    if (this.fontPicker && this.isFontPickerOpen) {
      const clickedInside =
        this.fontPicker.nativeElement.contains(targetElement);
      if (!clickedInside) {
        this.closeFontPicker();
        this.refreshUI();
      }
    }
  }

  onRemoveCustomFont(fontFamily: RiseFont, event: MouseEvent) {
    event.stopPropagation();
    try {
      this.customFontListManagementService.removeFont(fontFamily.url);
      this.customFonts = this.customFonts.filter(
        (font) => font.url !== fontFamily.url,
      );
      if (this.font === fontFamily.font) {
        this.font = this.defaultFont.font;
        this.fontName = this.defaultFont.name;
        this.fontUrl = this.defaultFont.url;
        this.save('font');
      }
    } catch (error) {
      console.error('Failed to remove custom font:', {
        url: fontFamily.url,
        error,
      });
    }
  }
}
